import React, { useEffect, useState } from 'react';
import { Box, Typography, Modal as LegoModal, Dropdown, TextArea, Button } from '@lucid/lego';
import type { Theme } from '../../utils';
import { getCancellationReasonOptions } from '../utils';
import styles from './CancelAppointmentModal.module.scss';

type CancelAppointmentModalProps = {
  isOpen: boolean;
  theme: Theme;
  labels: Record<string, string>;
  isLoading?: boolean;
  onKeepAppointment: () => void;
  onConfirmCancel: (reason: string, notes: string) => void;
};

const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  isOpen,
  theme,
  labels,
  isLoading = false,
  onKeepAppointment,
  onConfirmCancel,
}) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setNotes('');
    }
  }, [isOpen]);

  return (
    <LegoModal
      dataTestId="cancel-appointment-modal"
      isOpen={isOpen}
      onClose={onKeepAppointment}
      title={labels?.['testDrive.cancelAppointmentTitle'] ?? 'Cancel Test Drive'}
      theme={theme}
      size="small"
    >
      <Box className={styles.content}>
        <Box className={styles.field}>
          <Typography variant="body" size="small" color="gray-5" theme={theme}>
            {labels?.['testDrive.cancellationReason'] ?? 'Cancellation Reason'}
          </Typography>
          <Dropdown
            theme={theme}
            size="medium"
            options={getCancellationReasonOptions(labels)}
            selectedOption={reason}
            handleSelect={setReason}
            placeholder={labels?.['testDrive.cancellationReasonPlaceholder'] ?? 'Select a reason'}
            required
            gaComponent="cancel-appointment-reason-dropdown"
          />
        </Box>
        <Box className={styles.field}>
          <TextArea
            name="cancellationNotes"
            theme={theme}
            label={labels?.['form.notes'] ?? 'Notes'}
            placeholder={labels?.['form.notesPlaceholder'] ?? 'Enter Notes'}
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Box>
        <Box className={styles.footer}>
          <Button
            type="secondary"
            color="neutral"
            theme={theme}
            size="medium"
            isFluid
            label={labels?.['testDrive.keepAppointment']?.toUpperCase() ?? 'KEEP APPOINTMENT'}
            handleClick={onKeepAppointment}
            disabled={isLoading}
            gaComponent="cancel-appointment-keep-button"
            dataTestId="keep-appointment-button"
          />
          <Button
            type="primary"
            color="neutral"
            theme={theme}
            size="medium"
            isFluid
            label={
              isLoading
                ? (labels?.['form.saving'] ?? 'SAVING...')
                : (labels?.['testDrive.cancelAppointment']?.toUpperCase() ?? 'CANCEL APPOINTMENT')
            }
            handleClick={() => onConfirmCancel(reason, notes)}
            disabled={isLoading || !reason}
            gaComponent="cancel-appointment-confirm-button"
            dataTestId="confirm-cancel-appointment-button"
          />
        </Box>
      </Box>
    </LegoModal>
  );
};

export default CancelAppointmentModal;
